/**
 * Branching Service (POC)
 *
 * One-level question branching: a branchable question routes answers to
 * "branch child" questions that sit right after it in the SurveyJS page and
 * carry a generated `visibleIf` expression. The flow re-converges at the next
 * main-line question (questionPerPage mode auto-skips hidden questions).
 *
 * Branch metadata persists inside the surveyJson blob via custom SurveyJS
 * serializer properties — no backend changes needed.
 */

import {
  BranchRuleKind,
  BranchTargetKind,
  RatingOperator,
} from '@/contracts/enums/branching';
import { QuestionType } from '@/contracts/enums/questionTypes';
import {
  BranchRules,
  BranchRuleTarget,
  ChoiceBranchRules,
  MATRIX_ANY_ROW,
  MatrixBranchRules,
  MatrixValueCondition,
  RatingBranchRules,
} from '@/contracts/models/survey-branching';
import { Serializer } from 'survey-core';

export const BRANCH_PARENT_ID_PROPERTY = 'branchParentId';
export const BRANCH_RULES_PROPERTY = 'branchRules';
export const MAX_BRANCH_CHILDREN = 4;

const BRANCHABLE_TYPES = new Set<QuestionType>([
  QuestionType.Boolean,
  QuestionType.RadioGroup,
  QuestionType.Dropdown,
  QuestionType.Checkbox,
  QuestionType.MultiSelectDropdown,
  QuestionType.Rating,
  QuestionType.Matrix,
]);

const MULTI_SELECT_TYPES = new Set<QuestionType>([
  QuestionType.Checkbox,
  QuestionType.MultiSelectDropdown,
]);

let registered = false;

/**
 * Registers the custom question properties with the SurveyJS serializer so
 * they round-trip through Model.toJSON()/new Model(json). Must run before any
 * Model is created in the builder.
 */
export function registerBranchingProperties(): void {
  if (registered) return;
  registered = true;

  Serializer.addProperty('question', {
    name: BRANCH_PARENT_ID_PROPERTY,
    type: 'string',
    default: '',
    visible: false,
    isSerializable: true,
  });
  Serializer.addProperty('question', {
    name: BRANCH_RULES_PROPERTY,
    type: 'string',
    default: '',
    visible: false,
    isSerializable: true,
  });
}

export function isBranchableType(type: QuestionType): boolean {
  return BRANCHABLE_TYPES.has(type);
}

/**
 * Boolean rules have exactly two outcomes (true / false), so extra children
 * would be unreachable. Choice, rating and matrix rules map many conditions
 * (per choice / per threshold / per column) and can route to more children.
 */
export function getMaxBranchChildren(type: QuestionType): number {
  return type === QuestionType.Boolean ? 2 : MAX_BRANCH_CHILDREN;
}

/** 0 -> A, 1 -> B, ... */
export function getBranchLetter(childIndex: number): string {
  return String.fromCharCode(65 + childIndex);
}

/** e.g. parent question 1, second child -> "1B" */
export function getBranchChildLabel(
  parentNumber: number,
  childIndex: number
): string {
  return `${parentNumber}${getBranchLetter(childIndex)}`;
}

/**
 * Normalizes matrix rules to the single-row shape ({row, conditions}). Already
 * new-shaped rules pass through. The earlier cell-level shape (cellTargets with
 * a per-condition row) collapses to one row + value conditions: a single shared
 * row is preserved, mixed rows fall back to "any row".
 */
function migrateMatrixRules(
  parsed: Record<string, unknown>
): MatrixBranchRules {
  if (typeof parsed.row === 'string' && Array.isArray(parsed.conditions)) {
    return {
      kind: BranchRuleKind.Matrix,
      row: parsed.row,
      conditions: parsed.conditions as MatrixValueCondition[],
    };
  }
  if (Array.isArray(parsed.cellTargets)) {
    const cells = parsed.cellTargets as Array<{
      row?: unknown;
      column?: unknown;
      target?: unknown;
    }>;
    const distinctRows = new Set(
      cells
        .map(cell => (typeof cell.row === 'string' ? cell.row : ''))
        .filter(Boolean)
    );
    const row = distinctRows.size === 1 ? [...distinctRows][0] : MATRIX_ANY_ROW;
    const conditions = cells
      .filter(cell => typeof cell.column === 'string')
      .map(cell => ({
        column: cell.column as string,
        target: cell.target as BranchRuleTarget,
      }));
    return { kind: BranchRuleKind.Matrix, row, conditions };
  }
  return { kind: BranchRuleKind.Matrix, row: MATRIX_ANY_ROW, conditions: [] };
}

/**
 * Migrates a legacy threshold rating rule
 * ({threshold, atOrAboveTarget, belowTarget}) to the conditions shape.
 * Already-migrated rules pass through.
 */
function migrateRatingRules(
  parsed: Record<string, unknown>
): RatingBranchRules {
  if (Array.isArray(parsed.conditions)) {
    return {
      kind: BranchRuleKind.Rating,
      conditions: parsed.conditions as RatingBranchRules['conditions'],
    };
  }
  const threshold = typeof parsed.threshold === 'number' ? parsed.threshold : 0;
  const conditions: RatingBranchRules['conditions'] = [];
  if (parsed.atOrAboveTarget) {
    conditions.push({
      operator: RatingOperator.GreaterOrEqual,
      value: threshold,
      target: parsed.atOrAboveTarget as BranchRuleTarget,
    });
  }
  if (parsed.belowTarget) {
    conditions.push({
      operator: RatingOperator.LessThan,
      value: threshold,
      target: parsed.belowTarget as BranchRuleTarget,
    });
  }
  return { kind: BranchRuleKind.Rating, conditions };
}

export function parseBranchRules(raw: unknown): BranchRules | undefined {
  if (typeof raw !== 'string' || raw === '') return undefined;
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return undefined;
    }
    if (parsed.kind === BranchRuleKind.Matrix) {
      return migrateMatrixRules(parsed);
    }
    if (parsed.kind === BranchRuleKind.Rating) {
      return migrateRatingRules(parsed);
    }
    return parsed as unknown as BranchRules;
  } catch {
    return undefined;
  }
}

export function serializeBranchRules(rules: BranchRules): string {
  return JSON.stringify(rules);
}

const nextTarget = (): BranchRuleTarget => ({
  kind: BranchTargetKind.NextQuestion,
});

const childTarget = (childId: string): BranchRuleTarget => ({
  kind: BranchTargetKind.BranchChild,
  childId,
});

/**
 * Snapshot of the parent question fields branching cares about. Plain data so
 * the expression builder stays decoupled from SurveyJS question instances.
 */
export interface BranchParentSnapshot {
  name: string;
  type: QuestionType;
  // Option VALUES (the stable ids), NOT display text — rule keys and the
  // generated visibleIf reference options by value.
  choices?: string[];
  rows?: string[];
  columns?: string[];
  rateMin?: number;
  rateMax?: number;
}

/**
 * Default rules when branching is first enabled: route the "positive" path
 * (yes / at-or-above-midpoint) to the first child, everything else continues
 * to the next main-line question.
 */
export function createDefaultBranchRules(
  parent: BranchParentSnapshot,
  firstChildId: string
): BranchRules {
  switch (parent.type) {
    case QuestionType.Boolean:
      return {
        kind: BranchRuleKind.Boolean,
        trueTarget: childTarget(firstChildId),
        falseTarget: nextTarget(),
      };
    case QuestionType.Rating: {
      const min = parent.rateMin ?? 1;
      const max = parent.rateMax ?? 5;
      return {
        kind: BranchRuleKind.Rating,
        conditions: [
          {
            operator: RatingOperator.GreaterOrEqual,
            value: Math.ceil((min + max) / 2),
            target: childTarget(firstChildId),
          },
        ],
      };
    }
    case QuestionType.Matrix: {
      const firstColumn = parent.columns?.[0];
      return {
        kind: BranchRuleKind.Matrix,
        row: parent.rows?.[0] ?? MATRIX_ANY_ROW,
        conditions: firstColumn
          ? [{ column: firstColumn, target: childTarget(firstChildId) }]
          : [],
      };
    }
    default:
      return {
        kind: BranchRuleKind.Choice,
        choiceTargets: {},
      };
  }
}

function escapeExpressionValue(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
}

function buildValueListCondition(
  questionRef: string,
  values: string[],
  useContains: boolean
): string {
  const escaped = values.map(v => `'${escapeExpressionValue(v)}'`);
  if (useContains) {
    return escaped.map(v => `({${questionRef}} contains ${v})`).join(' or ');
  }
  if (escaped.length === 1) {
    return `{${questionRef}} = ${escaped[0]}`;
  }
  return `{${questionRef}} anyof [${escaped.join(', ')}]`;
}

function collectTargetValues(
  targets: Record<string, BranchRuleTarget>,
  childId: string,
  presentValues: string[]
): string[] {
  const present = new Set(presentValues);
  return Object.entries(targets)
    .filter(
      ([value, target]) =>
        target.kind === BranchTargetKind.BranchChild &&
        target.childId === childId &&
        present.has(value)
    )
    .map(([value]) => value);
}

/**
 * Builds the SurveyJS visibleIf expression for one branch child.
 * Returns 'false' when no rule routes to the child (never shown until mapped).
 */
export function buildChildVisibleIf(
  parent: BranchParentSnapshot,
  rules: BranchRules,
  childId: string
): string {
  switch (rules.kind) {
    case BranchRuleKind.Boolean: {
      const conditions: string[] = [];
      if (
        rules.trueTarget.kind === BranchTargetKind.BranchChild &&
        rules.trueTarget.childId === childId
      ) {
        conditions.push(`{${parent.name}} = true`);
      }
      if (
        rules.falseTarget.kind === BranchTargetKind.BranchChild &&
        rules.falseTarget.childId === childId
      ) {
        conditions.push(`{${parent.name}} = false`);
      }
      return conditions.length > 0 ? conditions.join(' or ') : 'false';
    }
    case BranchRuleKind.Rating: {
      const conditions = rules.conditions
        .filter(
          condition =>
            condition.target.kind === BranchTargetKind.BranchChild &&
            condition.target.childId === childId
        )
        .map(
          condition =>
            `({${parent.name}} ${condition.operator} ${condition.value})`
        );
      return conditions.length > 0 ? conditions.join(' or ') : 'false';
    }
    case BranchRuleKind.Choice: {
      const choices = parent.choices ?? [];
      const values = collectTargetValues(rules.choiceTargets, childId, choices);
      if (values.length === 0) return 'false';
      return buildValueListCondition(
        parent.name,
        values,
        MULTI_SELECT_TYPES.has(parent.type)
      );
    }
    case BranchRuleKind.Matrix: {
      const allRows = parent.rows ?? [];
      const columns = new Set(parent.columns ?? []);
      // The rule targets one row, or every row when "any row" is selected.
      const targetRows =
        rules.row === MATRIX_ANY_ROW
          ? allRows
          : allRows.includes(rules.row)
            ? [rules.row]
            : [];
      // One clause per (targeted row × matched column value) routing to this
      // child; present columns only.
      const conditions = rules.conditions
        .filter(
          condition =>
            columns.has(condition.column) &&
            condition.target.kind === BranchTargetKind.BranchChild &&
            condition.target.childId === childId
        )
        .flatMap(condition =>
          targetRows.map(
            row =>
              `({${parent.name}.${row}} = '${escapeExpressionValue(condition.column)}')`
          )
        );
      return conditions.length > 0 ? conditions.join(' or ') : 'false';
    }
  }
}

/**
 * Rewrites any rule target pointing at a removed child back to "next
 * question". Returns the same object when nothing changed.
 */
export function retargetRulesAfterChildRemoval(
  rules: BranchRules,
  removedChildId: string
): BranchRules {
  const fix = (target: BranchRuleTarget): BranchRuleTarget =>
    target.kind === BranchTargetKind.BranchChild &&
    target.childId === removedChildId
      ? nextTarget()
      : target;

  switch (rules.kind) {
    case BranchRuleKind.Boolean:
      return {
        ...rules,
        trueTarget: fix(rules.trueTarget),
        falseTarget: fix(rules.falseTarget),
      };
    case BranchRuleKind.Rating:
      return {
        ...rules,
        conditions: rules.conditions.map(condition => ({
          ...condition,
          target: fix(condition.target),
        })),
      };
    case BranchRuleKind.Choice: {
      const choiceTargets: ChoiceBranchRules['choiceTargets'] = {};
      Object.entries(rules.choiceTargets).forEach(([key, target]) => {
        choiceTargets[key] = fix(target);
      });
      return { ...rules, choiceTargets };
    }
    case BranchRuleKind.Matrix:
      return {
        ...rules,
        conditions: rules.conditions.map(condition => ({
          ...condition,
          target: fix(condition.target),
        })),
      };
  }
}

/**
 * Cleans value-keyed rules after the parent's choices/columns change: drops
 * choiceTargets / columnTargets whose key value no longer exists. Returns
 * undefined when nothing changed.
 */
export function pruneRulesAfterStructureChange(
  rules: BranchRules,
  parent: BranchParentSnapshot
): BranchRules | undefined {
  // Drops value-keyed targets whose key no longer exists; undefined = unchanged.
  const prune = (
    targets: Record<string, BranchRuleTarget>,
    presentValues: string[] | undefined
  ): Record<string, BranchRuleTarget> | undefined => {
    const present = new Set(presentValues ?? []);
    const kept = Object.entries(targets).filter(([value]) =>
      present.has(value)
    );
    return kept.length === Object.keys(targets).length
      ? undefined
      : Object.fromEntries(kept);
  };

  if (rules.kind === BranchRuleKind.Choice) {
    const choiceTargets = prune(rules.choiceTargets, parent.choices);
    return choiceTargets ? { ...rules, choiceTargets } : undefined;
  }
  if (rules.kind === BranchRuleKind.Matrix) {
    const rows = new Set(parent.rows ?? []);
    const columns = new Set(parent.columns ?? []);
    // A deleted target row falls back to "any row"; conditions whose column
    // no longer exists are dropped.
    const row =
      rules.row === MATRIX_ANY_ROW || rows.has(rules.row)
        ? rules.row
        : MATRIX_ANY_ROW;
    const conditions = rules.conditions.filter(condition =>
      columns.has(condition.column)
    );
    const changed =
      row !== rules.row || conditions.length !== rules.conditions.length;
    return changed ? { ...rules, row, conditions } : undefined;
  }
  if (rules.kind === BranchRuleKind.Rating) {
    const { rateMin, rateMax } = parent;
    if (rateMin === undefined || rateMax === undefined || rateMin > rateMax) {
      return undefined;
    }
    const conditions = rules.conditions.filter(
      condition => condition.value >= rateMin && condition.value <= rateMax
    );
    return conditions.length !== rules.conditions.length
      ? { ...rules, conditions }
      : undefined;
  }
  return undefined;
}
